<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import Loading from '../loading/index.vue';

export interface Props {
  variant?:
    | 'border-back'
    | 'stroke'
    | 'text'
    | 'text-purple'
    | 'text-danger'
    | 'text-warning'
    | 'back'
    | 'black'
    | 'purple'
    | 'warning'
    | 'border-purple'
    | 'danger'
    | 'warning-10'
    | 'link';

  size?: 'default' | 'compact' | 'large' | 'middle' | 'mini' | 'none';
  class?: HTMLAttributes['class'];
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  blank?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'purple',
  size: 'default',
  type: 'button',
  disabled: false,
  loading: false,
  blank: false,
});

const emit = defineEmits<{
  (event: 'click', payload: MouseEvent): void;
}>();

const handleClick = (event: MouseEvent) => {
  if (props.disabled || props.loading) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  emit('click', event);
};
</script>

<template>
  <button
    :type="type"
    :disabled="disabled"
    :class="[`variant-${variant}`, `size-${size}`, props.class, { blank: blank }]"
    @click="handleClick"
  >
    <template v-if="$slots.icon">
      <Loading v-if="loading" type="spinner" :size="16" />
      <slot v-else name="icon"> </slot>
      <slot />
    </template>
    <template v-else>
      <div class="wrapper" :data-loading="loading">
        <Loading v-if="loading" class="loading" type="spinner" :size="16" />
        <div class="content">
          <slot />
        </div>
      </div>
    </template>
  </button>
</template>

<style scoped lang="scss">
@use '../../styles/mixins.scss' as *;

/* ==================== 基础样式 ==================== */
button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: var(--x-radius-medium);
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid transparent;
  outline: none;
  transition: all 0.2s ease-in-out;
}

/* ==================== 内部结构样式 ==================== */
.blank {
  width: 100%;
}

.wrapper {
  position: relative;
}

.wrapper[data-loading='true'] .content {
  opacity: 0;
}

.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.content {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* ==================== 尺寸变体 ==================== */
.size-mini {
  height: 24px;
  font-size: 12px;
  padding-inline: 8px;
  gap: 2px;
}

.size-mini :deep(svg) {
  width: 12px;
  height: 12px;
}

.size-compact {
  height: 28px;
  font-size: 12px;
  padding-inline: 12px;
  gap: 0px;
}

.size-compact :deep(svg) {
  width: 16px;
  height: 16px;
}

.size-middle {
  height: 32px;
  padding-inline: 20px;
}

.size-default {
  height: 36px;
  min-width: fit-content;
  padding: 7px 19px;
}

.size-large {
  height: 44px;
  font-size: var(--x-text-14-size);
  padding-inline: 20px;

  @include mobile {
    height: responsive-size(44px);
    font-size: responsive-size(var(--x-text-14-size));
    padding-inline: responsive-size(16px);
  }
}

/* ==================== 颜色变体 - 实心按钮 ==================== */
.variant-purple {
  background-color: var(--x-purple-6);
  color: white;
}

.variant-purple:hover:not(:disabled) {
  background-color: var(--x-purple-7);
}

.variant-purple:disabled {
  background-color: var(--x-purple-4);
  color: var(--x-back-1);
}

.variant-black {
  background-color: var(--x-text-3);
  color: white;
}

.variant-black:hover:not(:disabled) {
  background-color: var(--x-text-5);
}

.variant-black:disabled {
  background-color: var(--x-text-9);
  color: white;
}

.variant-back {
  background-color: var(--x-back-4);
  color: var(--x-text-1);
}

.variant-back:hover:not(:disabled) {
  background-color: var(--x-back-5);
}

.variant-back:disabled {
  background-color: var(--x-back-4);
  color: var(--x-text-8);
}

.variant-warning {
  background-color: var(--x-warning-3);
  color: var(--x-warning-10);
}

.variant-warning:hover:not(:disabled) {
  background-color: var(--x-warning-4);
}

.variant-warning:disabled {
  background-color: var(--x-warning-3);
  color: var(--x-warning-10);
}

.variant-warning-10 {
  background-color: var(--x-warning-10);
  color: var(--x-warning-3);
}

.variant-warning-10:hover:not(:disabled) {
  background-color: var(--x-warning-9);
}

.variant-warning-10:disabled {
  background-color: var(--x-back-4);
  color: var(--x-text-8);
}

.variant-danger {
  background-color: var(--x-error-7);
  color: white;
  border: none;
}

.variant-danger:hover:not(:disabled) {
  background-color: var(--x-error-6);
}

.variant-danger:disabled {
  background-color: var(--x-error-4);
  color: white;
}

/* ==================== 颜色变体 - 文本按钮 ==================== */
.variant-text {
  background-color: transparent;
  color: var(--x-text-1);
}

.variant-text:hover:not(:disabled) {
  background-color: var(--x-back-4);
}

.variant-text:disabled {
  background-color: transparent;
  color: var(--x-text-8);
}

.variant-text-purple {
  background-color: transparent;
  color: var(--x-purple-6);
}

.variant-text-purple:hover:not(:disabled) {
  color: var(--x-purple-7);
}

.variant-text-purple:disabled {
  color: var(--x-purple-4);
}

.variant-text-warning {
  background-color: transparent;
  color: var(--x-warning-7);
}

.variant-text-warning:hover:not(:disabled) {
  color: var(--x-warning-6);
}

.variant-text-warning:disabled {
  color: var(--x-warning-5);
}

.variant-text-danger {
  background-color: var(--x-back-1);
  color: var(--x-error-7);
  border: none;
}

.variant-text-danger:hover:not(:disabled) {
  background-color: var(--x-error-2);
}

.variant-text-danger:disabled {
  color: var(--x-error-4);
}

/* ==================== 颜色变体 - 边框按钮 ==================== */
.variant-border-back {
  background-color: transparent;
  color: var(--x-text-1);
  border-color: var(--x-back-4);
}

.variant-border-back:hover:not(:disabled) {
  background-color: var(--x-back-4);
  border-color: var(--x-back-4);
}

.variant-border-back:disabled {
  background-color: transparent;
  color: var(--x-text-8);
  border-color: var(--x-back-4);
}

/* 描边按钮（Figma 规范：白底 + 描边，hover 灰底 + 深描边） */
.variant-stroke {
  background-color: var(--x-back-1);
  color: var(--x-text-1);
  border-color: var(--x-back-4);
}

.variant-stroke:hover:not(:disabled) {
  background-color: var(--x-back-4);
  border-color: var(--x-back-5);
}

.variant-stroke:disabled {
  background-color: var(--x-back-1);
  color: var(--x-text-8);
  border-color: var(--x-back-4);
}

.variant-border-purple {
  background-color: transparent;
  color: var(--x-purple-6);
  border-color: var(--x-purple-6);
}

.variant-border-purple:hover:not(:disabled) {
  background-color: var(--x-purple-1);
}

.variant-border-purple:disabled {
  color: var(--x-text-4);
  border-color: var(--x-purple-4);
}

/* ==================== 颜色变体 - 链接按钮 ==================== */
.variant-link {
  background-color: transparent;
  color: var(--x-text-1);
  border: none;
  padding: 0;
}

.variant-link:hover:not(:disabled) {
  text-decoration: underline;
}

/* ==================== 状态样式 ==================== */
button:disabled {
  cursor: not-allowed;
}

button:active:not(:disabled) {
  transform: scale(0.97);
}

button:focus-visible {
  box-shadow: 0 0 0 2px var(--x-purple-3);
}

/* ==================== 工具类 ==================== */
.btn-full-width {
  width: 100%;
}

.btn-icon-only {
  padding-inline: 9px;
  aspect-ratio: 1;
}
</style>
